export type ID_VALIDATION_RULE_KEYS =
  | 'MIN_LENGTH_5'
  | 'NUMERICAL_ID'
  | 'OFP_ID_WITH_OPTIONAL_SLUG'
  | 'CORE_ID_OR_OFP_ID'
  | 'CORE_ID_WITH_OPTIONAL_SLUG'
  | 'PEOPLE_PROFILE_URL'
  | 'UUID_V4'
  | 'ATHLETE_UUID_URL'

/**
 * @example 12345
 * @example 12345-the-best-event-ever
 */
export const CORE_ID_RULES: ID_VALIDATION_RULE_KEYS[] = ['CORE_ID_WITH_OPTIONAL_SLUG']

/**
 * @example 9v4D4eu4V0tNuhf5
 * @example 99v4D4eu4V0tNuhf5
 * @example 99v4D4eu4V0tNuhf5-the-best-event-ever
 * @example 999v4D4eu4V0tNuhf5
 * @example 999v4D4eu4V0tNuhf5-the-best-event-ever
 */
export const OFP_ID_RULES: ID_VALIDATION_RULE_KEYS[] = ['OFP_ID_WITH_OPTIONAL_SLUG']

/**
 * @example 9v4D4eu4V0tNuhf5
 * @example 9v4D4eu4V0tNuhf5-the-best-event-ever
 * @example 12345
 * @example 12345-the-best-event-ever
 */
export const CORE_ID_OR_OFP_ID_RULES: ID_VALIDATION_RULE_KEYS[] = ['CORE_ID_OR_OFP_ID']

export const VALIDATION_RULES_FOR_ID_PARAMS: Record<ID_VALIDATION_RULE_KEYS, RegExp> = {
  MIN_LENGTH_5: /^.{5,}$/,
  NUMERICAL_ID: /^\d+$/,
  CORE_ID_WITH_OPTIONAL_SLUG: /^[0-9]{5,}(-[a-zA-Z0-9]+)*$/,
  OFP_ID_WITH_OPTIONAL_SLUG: /^[0-9]{1,3}[a-zA-Z0-9]{15}(-[a-zA-Z0-9]+)*$/,
  CORE_ID_OR_OFP_ID: /^[0-9]{1,3}[a-zA-Z0-9]{15}(-[a-zA-Z0-9]+)*$|^[0-9]{5,}(-[a-zA-Z0-9]+)*$/,
  /**
   * Matches people profile URLs with core ID and optional slug/path/query params
   * @example /people/12345
   * @example /people/12345-john-doe
   * @example /people/12345-john-doe/videos
   * @example /people/12345?tab=results
   */
  PEOPLE_PROFILE_URL: /^\/people\/(\d+)(?:-[a-zA-Z-]+)?(\/[a-zA-Z-]+)?(\?.*)?$/,
  /**
   * Matches UUID version 4 format (case-insensitive)
   * @example 07a06c7b-aa9b-4ae0-8242-789ca245ca50
   * @example 6591A7E1-3195-4C30-BA79-EFAA06C22980
   */
  UUID_V4: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  /**
   * Matches athlete URLs with UUID and captures the UUID
   * @example /athletes/07a06c7b-aa9b-4ae0-8242-789ca245ca50/results
   * @example /athletes/6591a7e1-3195-4c30-ba79-efaa06c22980/placements
   * @example /07a06c7b-aa9b-4ae0-8242-789ca245ca50/bio (relative path)
   */
  ATHLETE_UUID_URL: /^\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i
}

/**
 * Utility functions for UUID validation and athlete URL matching
 */
export const UUID_UTILS = {
  /**
   * Check if a string is a valid UUID v4
   */
  isValidUuid: (value: string): boolean => {
    return VALIDATION_RULES_FOR_ID_PARAMS.UUID_V4.test(value)
  },

  /**
   * Extract UUID from athlete URL path (relative path like /uuid/results)
   */
  extractUuidFromAthletePath: (relativePath: string): string | null => {
    const match = relativePath.match(VALIDATION_RULES_FOR_ID_PARAMS.ATHLETE_UUID_URL)
    return match ? match[1] : null
  },

  /**
   * Check if a URL matches the athlete UUID pattern (for full URLs like /athletes/uuid/...)
   */
  isAthleteUuidUrl: (url: string): boolean => {
    return /^\/athletes\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(url)
  },

  /**
   * Extract UUID from full athlete URL (like /athletes/uuid/results)
   */
  extractUuidFromAthleteUrl: (url: string): string | null => {
    const match = url.match(/\/athletes\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)
    return match ? match[1] : null
  }
}
